import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminPagination } from '@/components/AdminPagination';
import { AdminPill, type AdminPillStatus } from '@/components/AdminPill';
import { AdminTableSkeletonRows } from '@/components/AdminTableSkeletonRows';
import { AdminTableToolbar } from '@/components/AdminTableToolbar';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { useAdminTablePagination } from '@/hooks/useAdminTablePagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { downloadCsv } from '@/lib/csv-download';
import { formatGstDate, formatGstDateTimeIso } from '@/lib/format';
import { parsePageParam, parsePageSizeParam } from '@/lib/pagination';
import {
  exportBuyerRequestsCsv,
  nextAllowedStatuses,
  updateBuyerRequestInternalNote,
  updateBuyerRequestStatus,
  type AdminBuyerRequestRowJoined,
  type AdminBuyerRequestStatus,
} from '@/lib/db/admin/requests';

import { useAdminRequests, type AdminRequestsFilter } from './AdminRequestsPage.data';

const FILTERS = ['all', 'new', 'in_progress', 'treated', 'closed'] as const;
type FilterKey = (typeof FILTERS)[number];

const FILTER_I18N_KEY: Record<FilterKey, string> = {
  all: 'admin.requests.filters.all',
  new: 'admin.requests.filters.new',
  in_progress: 'admin.requests.filters.inProgress',
  treated: 'admin.requests.filters.processed',
  closed: 'admin.requests.filters.closed',
};

const STATUS_PILL_I18N_KEY: Record<AdminBuyerRequestStatus, string> = {
  new: 'admin.common.newPill',
  in_progress: 'admin.common.inProgressPill',
  treated: 'admin.common.processedPill',
  closed: 'admin.common.closedPill',
};

const TYPE_I18N_KEY: Record<'interest' | 'callback', string> = {
  interest: 'admin.requests.types.interest',
  callback: 'admin.requests.types.callback',
};

const INTERNAL_NOTE_MAX = 1000;

function pillVariant(s: AdminBuyerRequestStatus): AdminPillStatus {
  if (s === 'in_progress') return 'IN_PROGRESS';
  if (s === 'treated') return 'PROCESSED';
  if (s === 'closed') return 'CLOSED';
  return 'NEW';
}

type StatusHistoryEntry = {
  at: string;
  from: string;
  to: string;
  by: string;
};

function isStatusHistoryEntry(v: unknown): v is StatusHistoryEntry {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.at === 'string' &&
    typeof o.from === 'string' &&
    typeof o.to === 'string' &&
    typeof o.by === 'string'
  );
}

function isPostgrestForwardViolation(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { code?: unknown }).code === '23514';
}

function postgrestErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

// Resolve a buyer's display name across the three request shapes: a joined
// account (authenticated), an anonymous lead (ADR-053 — contact_* columns), or
// a hard-deleted account (users NULL + not anonymous → generic label).
function buyerDisplayName(req: AdminBuyerRequestRowJoined, t: TFunction): string {
  const buyer = req.users;
  if (buyer) {
    return (
      [buyer.first_name, buyer.last_name].filter(Boolean).join(' ') ||
      buyer.email ||
      t('admin.requests.drawer.anonymous', 'Anonymous')
    );
  }
  if (req.is_anonymous) {
    return (
      req.contact_name?.trim() ||
      req.contact_email ||
      t('admin.requests.drawer.anonymous', 'Anonymous')
    );
  }
  return t('admin.requests.drawer.anonymous', 'Anonymous');
}

// Pre-fill the WhatsApp follow-up text with the buyer's first name (when
// available) and the request reference_id so the admin doesn't have to
// retype context. Mirrors the DealCard catalogue helper (PR #75). For an
// anonymous lead the first name comes from contact_name (ADR-053).
function composeFollowUpMessage(
  req: AdminBuyerRequestRowJoined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const anonFirstName = req.is_anonymous ? (req.contact_name?.trim().split(/\s+/)[0] ?? '') : '';
  return t('admin.requests.followUpMessage', {
    firstName: req.users?.first_name ?? anonFirstName,
    referenceId: req.reference_id,
  });
}

export function AdminRequestsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Staged status pending an explicit Validate click — mirrors the pattern
  // used by AdminSubmissionsPage so the admin always confirms a status
  // change instead of mutating server-side on the `<select>` change event.
  const [pendingStatus, setPendingStatus] = useState<AdminBuyerRequestStatus | ''>('');

  // URL is the pagination source of truth (read every render — Back/Forward
  // replays states natively). Writes go through useAdminTablePagination.
  const [searchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get('page'));
  const pageSize = parsePageSizeParam(searchParams.get('pageSize'));
  const debouncedSearch = useDebouncedValue(searchQuery);

  const queryFilter: AdminRequestsFilter = useMemo(
    () => ({
      status: filter === 'all' ? 'all' : filter,
      searchQuery: debouncedSearch,
      page,
      pageSize,
    }),
    [filter, debouncedSearch, page, pageSize],
  );

  const { rows, total, newCount, status, error, refetch } = useAdminRequests(queryFilter);

  const { goPage, setPageSize } = useAdminTablePagination({
    page,
    pageSize,
    total,
    isSuccess: status === 'success',
    filterKey: `${filter}|${debouncedSearch}`,
  });

  const selected: AdminBuyerRequestRowJoined | null = useMemo(
    () => (openId ? (rows.find((r) => r.id === openId) ?? null) : null),
    [openId, rows],
  );

  // The open drawer tracks a row of the CURRENT page: when a page / filter
  // change drops its row, release the selection. Only after a successful
  // load — a transient skeleton state must not slam the drawer shut.
  useEffect(() => {
    if (status !== 'success' || !openId) return;
    if (!rows.some((r) => r.id === openId)) setOpenId(null);
  }, [status, rows, openId]);

  // Sync the note draft with the selected row whenever the selection
  // changes or the row is refetched. This keeps the textarea in sync
  // with the latest server-side value (e.g. another admin edited it).
  useEffect(() => {
    setNoteDraft(selected?.internal_note ?? '');
  }, [selected?.id, selected?.internal_note]);

  // Reset the staged status whenever the selected request changes — opening
  // a different request must not carry over a pending choice from the
  // previously open one.
  useEffect(() => {
    setPendingStatus('');
  }, [selected?.id]);

  const handleChangeStatus = async (next: AdminBuyerRequestStatus) => {
    if (!selected) return;
    setIsChangingStatus(true);
    try {
      await updateBuyerRequestStatus(selected.id, next);
      toast.success(t('admin.requests.changeStatus.successToast', 'Status updated.'));
      setPendingStatus('');
      refetch();
    } catch (err) {
      if (isPostgrestForwardViolation(err)) {
        toast.error(
          t(
            'admin.requests.changeStatus.forbiddenToast',
            'Forbidden status transition (forward-only).',
          ),
        );
      } else {
        toast.error(t('admin.requests.errors.genericToast', 'Something went wrong.'));
      }
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selected) return;
    if (noteDraft.length > INTERNAL_NOTE_MAX) return;
    setIsSavingNote(true);
    try {
      const value = noteDraft.length === 0 ? null : noteDraft;
      await updateBuyerRequestInternalNote(selected.id, value);
      toast.success(t('admin.requests.internalNote.savedToast', 'Internal note saved.'));
      refetch();
    } catch {
      toast.error(t('admin.requests.errors.genericToast', 'Something went wrong.'));
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleRetryBitrix = () => {
    toast.info(
      t(
        'admin.requests.bitrix.retryDeferredToast',
        'Bitrix24 sync arrives in Phase C — manual retry not available yet.',
      ),
    );
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const dbFilter = filter === 'all' ? null : (filter as AdminBuyerRequestStatus);
      const csv = await exportBuyerRequestsCsv(dbFilter);
      downloadCsv(csv, `buyer-requests_${todayIsoDate()}.csv`);
      toast.success(t('admin.requests.export.successToast', 'CSV export ready.'));
    } catch (err) {
      const code = postgrestErrorCode(err);
      if (code === '54000') {
        toast.error(
          t('admin.requests.export.rateLimitedToast', 'Rate limit: max 5 exports / minute.'),
        );
      } else if (code === '42501') {
        toast.error(t('admin.requests.export.forbiddenToast', 'Only admins can export the CSV.'));
      } else {
        toast.error(t('admin.requests.errors.genericToast', 'Something went wrong.'));
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.requests.title')}</h1>
          <div className="admin-head__sub">
            {t('admin.requests.summary', { newCount, count: total })}
          </div>
        </div>
        <div className="admin-head__actions">
          <button
            type="button"
            className="admin-btn"
            onClick={handleExportCsv}
            disabled={isExporting || status !== 'success'}
          >
            {isExporting
              ? t('admin.requests.export.busy', 'Exporting…')
              : t('admin.requests.export.button', 'Export CSV')}
          </button>
        </div>
      </header>

      <AdminTableToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        // Reference-only contract (ADR-056 §4) — buyer/deal are embedded
        // resources, PostgREST cannot OR them with a top-level column.
        searchPlaceholder={t('admin.requests.searchPlaceholder')}
      >
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`admin-filter-tab${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(FILTER_I18N_KEY[key])}
          </button>
        ))}
      </AdminTableToolbar>

      {status === 'loading' && (
        <div className="admin-table-wrap" data-testid="admin-requests-loading" aria-busy="true">
          <table className="admin-table">
            <tbody>
              <AdminTableSkeletonRows columns={8} />
            </tbody>
          </table>
        </div>
      )}

      {status === 'error' && (
        <div className="admin-text-muted" data-testid="admin-requests-error" role="alert">
          {t('admin.requests.errors.list', 'Could not load requests.')}
          {error?.message ? ` (${error.message})` : null}
        </div>
      )}

      {status === 'success' && rows.length === 0 && (
        <div className="admin-text-muted" data-testid="admin-requests-empty">
          {t('admin.requests.empty.title', 'No buyer requests yet.')}
        </div>
      )}

      {status === 'success' && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table admin-table--sticky-actions">
            <thead>
              <tr>
                <th>{t('admin.requests.headers.date')}</th>
                <th>{t('admin.requests.headers.reference', 'Reference')}</th>
                <th>{t('admin.requests.headers.buyer')}</th>
                <th>{t('admin.requests.headers.deal')}</th>
                <th>{t('admin.requests.headers.type')}</th>
                <th>{t('admin.requests.headers.status')}</th>
                <th>{t('admin.requests.headers.bitrix')}</th>
                <th>{t('admin.requests.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((req) => {
                const buyerName = buyerDisplayName(req, t);
                return (
                  <tr key={req.id}>
                    <td className="admin-cell--mono">{formatGstDate(req.created_at)}</td>
                    <td className="admin-cell--mono">{req.reference_id}</td>
                    <td>
                      <span className="admin-cell-ellipsis" title={buyerName}>
                        {buyerName}
                      </span>
                    </td>
                    <td>
                      <span className="admin-cell-ellipsis" title={req.deals?.title ?? undefined}>
                        {req.deals?.title ?? '—'}
                      </span>
                    </td>
                    <td>{t(TYPE_I18N_KEY[req.type])}</td>
                    <td>
                      <AdminPill
                        status={pillVariant(req.status)}
                        label={t(STATUS_PILL_I18N_KEY[req.status])}
                      />
                    </td>
                    <td className="admin-cell--mono">
                      {req.bitrix24_lead_id ?? t('admin.requests.bitrix.notSynced', 'not synced')}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-cell-link"
                        onClick={() => setOpenId(req.id)}
                        aria-label={t('admin.requests.openDrawer', 'Open request')}
                      >
                        {t('admin.requests.open', 'Open')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {status !== 'error' && (
        <AdminPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={goPage}
          onPageSizeChange={setPageSize}
          idPrefix="admin-requests"
        />
      )}

      {selected ? (
        <div className="admin-detail-modal" role="presentation" onClick={() => setOpenId(null)}>
          <div
            className="admin-detail-modal__inner"
            role="dialog"
            aria-modal="true"
            aria-label={t('admin.requests.drawer.aria', 'Request detail')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-drawer__head">
              <span className="admin-drawer__id">{selected.reference_id}</span>
              <button
                type="button"
                className="admin-drawer__close"
                onClick={() => setOpenId(null)}
                aria-label={t('admin.requests.drawer.close', 'Close drawer')}
              >
                ×
              </button>
            </div>

            <div className="admin-drawer__title">
              {`${buyerDisplayName(selected, t)} · ${selected.deals?.title ?? '—'}`}
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.date', 'Date')}
              </div>
              <div className="admin-drawer__row-value">
                {formatGstDateTimeIso(selected.created_at)}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.contact', 'Buyer contact')}
              </div>
              <div className="admin-drawer__row-value">
                {selected.users
                  ? `${selected.users.email ?? '—'} · ${selected.users.phone ?? '—'}`
                  : selected.is_anonymous
                    ? `${selected.contact_email ?? '—'} · ${selected.contact_phone ?? '—'}`
                    : t('admin.requests.drawer.contactRemoved', 'Contact info removed')}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.requestPhone', 'Phone for this request')}
              </div>
              <div className="admin-drawer__row-value">{selected.contact_phone ?? '—'}</div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.deal', 'Deal')}
              </div>
              <div className="admin-drawer__row-value">
                {selected.deals ? (
                  <Link to={`/admin/deals/${selected.deals.id}/edit`}>
                    {selected.deals.title} · {selected.deals.zone}
                  </Link>
                ) : (
                  '—'
                )}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.type', 'Type')}
              </div>
              <div className="admin-drawer__row-value">{t(TYPE_I18N_KEY[selected.type])}</div>
            </div>

            {selected.message ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.requests.drawer.message', 'Message')}
                </div>
                <div className="admin-drawer__row-value">{selected.message}</div>
              </div>
            ) : null}

            {selected.type === 'callback' ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.requests.drawer.callback', 'Callback slot')}
                </div>
                <div className="admin-drawer__row-value">
                  {selected.callback_slot ?? '—'}
                  {selected.callback_date_preference
                    ? ` · ${selected.callback_date_preference}`
                    : ''}
                </div>
              </div>
            ) : null}

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.statusHistory', 'Status history')}
              </div>
              <div className="admin-drawer__row-value">
                {(() => {
                  const raw = (selected.status_history ?? []) as unknown;
                  const entries = Array.isArray(raw) ? raw.filter(isStatusHistoryEntry) : [];
                  if (entries.length === 0) {
                    return (
                      <span className="admin-text-muted">
                        {t('admin.requests.drawer.statusHistoryEmpty', 'No transitions yet.')}
                      </span>
                    );
                  }
                  return (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {entries.map((e, i) => (
                        <li key={`${e.at}-${i}`}>
                          {formatGstDateTimeIso(e.at)} · {e.from} → {e.to}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.drawer.bitrix', 'Bitrix24')}
              </div>
              <div className="admin-drawer__row-value">
                {selected.bitrix24_lead_id ?? t('admin.requests.bitrix.notSynced', 'not synced')}
                {selected.bitrix24_sync_failed ? (
                  <div
                    role="alert"
                    style={{
                      color: 'var(--ddm-warning)',
                      marginTop: 6,
                    }}
                  >
                    {t('admin.requests.bitrix.failedBanner', 'Bitrix24 sync failed.')}
                    <button
                      type="button"
                      className="admin-btn admin-btn--outline"
                      style={{ marginLeft: 8 }}
                      onClick={handleRetryBitrix}
                      disabled
                      title={t(
                        'admin.requests.bitrix.retryDeferredTooltip',
                        'Bitrix24 sync arrives in Phase C',
                      )}
                    >
                      {t('admin.requests.bitrix.retry', 'Retry')}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.changeStatus.label', 'Change status')}
              </div>
              <div className="admin-drawer__row-value">
                {(() => {
                  const allowed = nextAllowedStatuses(selected.status);
                  if (allowed.length === 0) {
                    return (
                      <span className="admin-text-muted">
                        {t(
                          'admin.requests.changeStatus.terminal',
                          'Closed — no further transitions.',
                        )}
                      </span>
                    );
                  }
                  return (
                    <select
                      className="admin-select"
                      value={pendingStatus}
                      onChange={(e) =>
                        setPendingStatus(e.target.value as AdminBuyerRequestStatus | '')
                      }
                      disabled={isChangingStatus}
                      aria-label={t('admin.requests.changeStatus.label', 'Change status')}
                    >
                      <option value="" disabled>
                        {t('admin.requests.changeStatus.placeholder', 'Select…')}
                      </option>
                      {allowed.map((s) => (
                        <option key={s} value={s}>
                          {t(STATUS_PILL_I18N_KEY[s])}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            </div>

            {nextAllowedStatuses(selected.status).length > 0 ? (
              <div
                className="admin-drawer__row"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => pendingStatus && void handleChangeStatus(pendingStatus)}
                  disabled={!pendingStatus || isChangingStatus}
                  data-testid="admin-request-validate"
                >
                  {t('admin.requests.changeStatus.validateButton', 'Validate')}
                </button>
              </div>
            ) : null}

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.requests.internalNote.label', 'Internal note')}
              </div>
              <div className="admin-drawer__row-value">
                <textarea
                  className="admin-textarea"
                  rows={3}
                  maxLength={INTERNAL_NOTE_MAX}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder={t(
                    'admin.requests.internalNote.placeholder',
                    'Visible to admins only',
                  )}
                  aria-label={t('admin.requests.internalNote.label', 'Internal note')}
                />
                <div className="admin-text-muted" style={{ marginTop: 4 }}>
                  {t('admin.requests.internalNote.charCount', '{{count}} / {{max}}', {
                    count: noteDraft.length,
                    max: INTERNAL_NOTE_MAX,
                  })}
                </div>
                <button
                  type="button"
                  className="admin-btn"
                  style={{ marginTop: 8 }}
                  onClick={handleSaveNote}
                  disabled={
                    isSavingNote ||
                    noteDraft.length > INTERNAL_NOTE_MAX ||
                    noteDraft === (selected.internal_note ?? '')
                  }
                >
                  {t('admin.requests.internalNote.save', 'Save note')}
                </button>
              </div>
            </div>

            <div className="admin-drawer__actions">
              {selected.contact_phone || selected.users?.phone ? (
                <WhatsAppButton
                  size="md"
                  label={t('admin.requests.followUp')}
                  fullWidth
                  phone={selected.contact_phone ?? selected.users?.phone ?? ''}
                  message={composeFollowUpMessage(selected, t)}
                />
              ) : (
                <span className="admin-text-muted">
                  {t('admin.requests.drawer.phoneNone', 'Buyer has no phone on file.')}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="admin-text-muted" style={{ marginTop: 14 }}>
        {t('admin.requests.footnote')}
      </div>
    </>
  );
}
